import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, LocateFixed, X, Loader2, CalendarDays, MapPin, Share2, Languages, Wallet, Mail, ArrowRight, Ticket, Gift } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { PublicLayout } from "../components/PublicLayout";
import { EventCard } from "../components/EventCard";
import { PostalCodeInput } from "../components/PostalCodeInput";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { api, CATEGORIES, formatDateTime, FALLBACK_IMAGE, imageSrc } from "../lib/api";
import { withLang } from "../lib/i18n";
import { CATEGORY_ORDER, categoryStyle } from "../lib/categories";
import { useLang } from "../context/LanguageContext";

// Kategori-snarveier: ikon og farge hentes fra felles kategori-stil (lib/categories)
const CATEGORY_SHORTCUTS = CATEGORY_ORDER.map((label) => ({ label, ...categoryStyle(label) }));

export default function HomePage() {
  const { t } = useLang();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("Alle");
  // «Gratis arrangement»: skjul alle betalte. Delbar lenke: /?gratis åpner forsiden ferdig filtrert
  const [freeOnly, setFreeOnlyState] = useState(() => new URLSearchParams(window.location.search).has("gratis"));
  const setFreeOnly = (on) => {
    setFreeOnlyState(on);
    const params = new URLSearchParams(window.location.search);
    if (on) params.set("gratis", "1"); else params.delete("gratis");
    const qs = params.toString();
    window.history.replaceState(null, "", `${window.location.pathname}${qs ? `?${qs}` : ""}${window.location.hash}`);
  };
  const [postalCode, setPostalCode] = useState("");
  const [postalPlace, setPostalPlace] = useState(""); // poststed for valt postnummer (frå registeret) – vises i filter-brikka
  // Delbar lenke: ?kommune=X åpner hovedsiden ferdig filtrert på kommunen
  const [kommune, setKommune] = useState(
    () => new URLSearchParams(window.location.search).get("kommune") || ""
  );
  const [cityFilter, setCityFilter] = useState("");
  const [position, setPosition] = useState(null);
  const [radius, setRadius] = useState("50");
  const [locating, setLocating] = useState(false);
  const [visitorKommune, setVisitorKommune] = useState("");
  const [visitorFylke, setVisitorFylke] = useState("");
  const [fylkeKommuner, setFylkeKommuner] = useState([]);

  // Ved delt kommune-/gratis-lenke: rull ned til listen så filtreringen er synlig med en gang
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("kommune") || params.has("gratis")) {
      const t = setTimeout(() => {
        document.getElementById("arrangementer")?.scrollIntoView({ behavior: "smooth" });
      }, 600);
      return () => clearTimeout(t);
    }
  }, []);

  const shareKommune = async () => {
    // Del OG-lenken slik at sosiale medier viser egen tittel/bilde; mennesker sendes videre til filtrert forside
    const url = withLang(`${window.location.origin}/api/share/kommune/${encodeURIComponent(visitorKommune)}`);
    const title = t("Arrangementer i {kommune} – Right in", { kommune: visitorKommune });
    if (navigator.share) {
      try {
        await navigator.share({ title, url });
        return;
      } catch {
        return; // avbrutt av bruker
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("Lenke kopiert – del arrangementer i {kommune} med andre", { kommune: visitorKommune }));
    } catch {
      toast.error(t("Kunne ikke kopiere lenken – prøv igjen"));
    }
  };

  // Be om posisjon ved besøk slik at «Fremhevet» viser neste arrangement i besøkendes kommune
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { data } = await api.get("/geo/kommune", {
            params: { lat: pos.coords.latitude, lon: pos.coords.longitude },
          });
          if (data.kommune) setVisitorKommune(data.kommune);
          if (data.fylke) setVisitorFylke(data.fylke);
          if (Array.isArray(data.fylke_kommuner)) setFylkeKommuner(data.fylke_kommuner);
        } catch {
          // Stille fallback – fremhevet viser da neste kommende arrangement totalt
        }
      },
      () => {
        // Avslått/utilgjengelig posisjon: stille fallback uten feilmelding
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 600000 }
    );
  }, []);

  useEffect(() => {
    let active = true;
    const timer = setTimeout(() => {
      setLoading(true);
      const params = {
        search: search || undefined,
        postal_code: postalCode.trim() || undefined,
        kommune: kommune.trim() || undefined,
        city: cityFilter.trim() || undefined,
      };
      if (position) {
        params.lat = position.lat;
        params.lon = position.lon;
        params.radius_km = radius;
      }
      api
        .get("/events", { params })
        .then((res) => {
          if (active) {
            setEvents(res.data);
            setError("");
          }
        })
        .catch(() => active && setError(t("Kunne ikke laste arrangementer. Prøv igjen senere.")))
        .finally(() => active && setLoading(false));
    }, 300);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [search, postalCode, kommune, cityFilter, position, radius]);

  const useMyPosition = () => {
    if (!navigator.geolocation) {
      toast.error(t("Nettleseren din støtter ikke posisjonsdeling"));
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setPosition({ lat: pos.coords.latitude, lon: pos.coords.longitude });
        setLocating(false);
        toast.success(t("Viser arrangementer nær deg"));
      },
      () => {
        setLocating(false);
        toast.error(t("Kunne ikke hente posisjonen din. Sjekk at posisjonsdeling er tillatt i nettleseren."));
      },
      { enableHighAccuracy: false, timeout: 10000 }
    );
  };

  const clearPosition = () => setPosition(null);

  const hasLocationFilter = postalCode.trim() || kommune.trim() || cityFilter.trim() || position;

  // Gratis = Right in-arrangement uten pris, eller eksternt arrangement der arrangøren har oppgitt «Gratis»
  const isFreeEvent = (e) =>
    e.ticketing === "external" ? /gratis|free/i.test(e.external_price_text || "") : (e.max_price ?? 0) === 0;
  const baseEvents = useMemo(() => (freeOnly ? events.filter(isFreeEvent) : events), [events, freeOnly]);
  // Kategorifiltrering skjer klient-side slik at tellerne på pillene alltid er oppdatert
  const visibleEvents = useMemo(
    () => (category === "Alle" ? baseEvents : baseEvents.filter((e) => e.category === category)),
    [baseEvents, category]
  );
  const categoryCounts = useMemo(() => {
    const counts = {};
    for (const e of baseEvents) counts[e.category] = (counts[e.category] || 0) + 1;
    return counts;
  }, [baseEvents]);
  const goToEvents = (onlyFree) => {
    setFreeOnly(onlyFree);
    document.getElementById("arrangementer")?.scrollIntoView({ behavior: "smooth" });
  };
  // Delbar lenke rett til gratisarrangementene (rightin.no/?gratis)
  const shareFreeLink = async () => {
    // Delingssiden gir Facebook & co. egen tittel/bilde og sender folk videre til /?gratis
    const url = withLang(`${window.location.origin}/api/share/gratis`);
    const title = t("Gratis arrangementer – Right in");
    if (navigator.share) {
      try {
        await navigator.share({ title, url });
        return;
      } catch (e) {
        if (e?.name === "AbortError") return;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("Lenken er kopiert"), { description: url });
    } catch {
      toast.error(t("Kunne ikke kopiere lenken"));
    }
  };
  // Fremhevet: kun fremtidige arrangementer – helst neste i besøkendes kommune, ellers neste totalt
  const { featured, featuredIsLocal } = useMemo(() => {
    const now = Date.now();
    const upcoming = visibleEvents.filter((e) => e.date && new Date(e.date).getTime() >= now);
    if (visitorKommune) {
      const local = upcoming.find(
        (e) => (e.kommune || "").trim().toLowerCase() === visitorKommune.trim().toLowerCase()
      );
      if (local) return { featured: local, featuredIsLocal: true };
    }
    return { featured: upcoming[0], featuredIsLocal: false };
  }, [visibleEvents, visitorKommune]);

  // Alle kommende arrangementer i besøkendes kommune – med fylkes-fallback når kommunen er tom
  const { localEvents, localScope } = useMemo(() => {
    if (!visitorKommune && !visitorFylke) return { localEvents: [], localScope: null };
    const now = Date.now();
    const upcoming = events.filter((e) => e.date && new Date(e.date).getTime() >= now);
    const norm = (s) => (s || "").trim().toLowerCase();
    if (visitorKommune) {
      const inKommune = upcoming.filter((e) => norm(e.kommune) === norm(visitorKommune));
      if (inKommune.length) return { localEvents: inKommune, localScope: "kommune" };
    }
    if (visitorFylke && fylkeKommuner.length) {
      const fylkeSet = new Set(fylkeKommuner.map(norm));
      const inFylke = upcoming.filter((e) => fylkeSet.has(norm(e.kommune)));
      if (inFylke.length) return { localEvents: inFylke, localScope: "fylke" };
    }
    return { localEvents: [], localScope: null };
  }, [events, visitorKommune, visitorFylke, fylkeKommuner]);

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="hero-decor border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="lg:col-span-7 space-y-5"
          >
            <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.05]">
              {t("Enklere blir det ikke:")}<br />
              <span className="text-muted-foreground">{t("Hent billetter på et øyeblikk!")}</span>
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">
              {t("Finn konserter, teater, sport og mer – nær deg eller der du skal.")}
            </p>
            <div className="flex flex-wrap gap-3 pt-1">
              <Button
                size="lg"
                className="h-11 px-6"
                onClick={() => goToEvents(false)}
                data-testid="home-find-events-button"
              >
                <Ticket className="w-4 h-4 mr-2" />
                {t("Billettsalg")}
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="h-11 px-6 bg-background/70"
                onClick={() => goToEvents(true)}
                data-testid="home-free-events-button"
              >
                <Gift className="w-4 h-4 mr-2" />
                {t("Gratis arrangement")}
              </Button>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="lg:col-span-5 hidden lg:block"
          >
            {featured ? (
              <Link to={`/arrangement/${featured.id}`} className="block" data-testid="home-featured-event">
                <div className="rounded-2xl overflow-hidden border border-border bg-card shadow-lg card-lift">
                  <div className="aspect-[4/3] bg-muted">
                    <img
                      src={imageSrc(featured.image_url)}
                      alt={featured.title}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.currentTarget.src = FALLBACK_IMAGE; }}
                    />
                  </div>
                  <div className="p-5 space-y-2">
                    <Badge variant="secondary" className="gap-1" data-testid="home-featured-badge">
                      {featuredIsLocal && <MapPin className="w-3 h-3" />}
                      {featuredIsLocal ? t("Fremhevet i {kommune}", { kommune: visitorKommune }) : t("Fremhevet")}
                    </Badge>
                    <h3 className="font-heading font-semibold text-xl">{featured.title}</h3>
                    <p className="text-sm text-muted-foreground">{formatDateTime(featured.date)}</p>
                  </div>
                </div>
              </Link>
            ) : (
              <div className="rounded-2xl overflow-hidden border border-border bg-card shadow-lg" data-testid="home-hero-placeholder-card">
                <div className="aspect-[4/3] bg-muted">
                  <img
                    src={FALLBACK_IMAGE}
                    alt={t("Sommerfestival i dagslys med telt og fargerike vimpler")}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-5">
                  <h3 className="font-heading font-semibold text-xl">{t("Ditt neste arrangement?")}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{t("Registrer deg som arrangør og publiser på minutter.")}</p>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* I din kommune – lokale kommende arrangementer rett under det fremhevede kortet */}
      {localEvents.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 sm:pt-12" data-testid="home-local-section">
          <div className="flex flex-wrap items-center gap-2.5 mb-5">
            <h2 className="text-xl sm:text-2xl font-semibold tracking-tight">
              {localScope === "kommune" ? t("I din kommune") : t("I ditt fylke")}
            </h2>
            <Badge variant="secondary" className="gap-1" data-testid="home-local-kommune-badge">
              <MapPin className="w-3 h-3" /> {localScope === "kommune" ? visitorKommune : visitorFylke}
            </Badge>
            <span className="text-sm text-muted-foreground" data-testid="home-local-count">
              {localEvents.length === 1 ? t("1 arrangement") : t("{n} arrangementer", { n: localEvents.length })}
            </span>
            {localScope === "kommune" && (
              <Button
                variant="outline"
                size="sm"
                className="ml-auto h-8"
                onClick={shareKommune}
                data-testid="home-share-kommune-button"
              >
                <Share2 className="w-3.5 h-3.5 mr-1.5" /> {t("Del")}
              </Button>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="home-local-grid">
            {localEvents.map((event) => (
              <EventCard key={`local-${event.id}`} event={event} testIdPrefix="local-event-card" />
            ))}
          </div>
        </section>
      )}

      {/* Listing */}
      <section id="arrangementer" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12 scroll-mt-20">
        <div className="flex flex-col gap-4 mb-6">
          <div>
            <span className="block h-px w-10 bg-brand/60 mb-3" aria-hidden="true" />
            <h2 className="text-xl sm:text-2xl font-semibold tracking-tight" data-testid="home-listing-title">
              {freeOnly ? t("Kommende gratisarrangementer") : t("Kommende arrangementer")}
            </h2>
          </div>

          {/* Kategori-snarveier: klikkbare knapper med egen dempet farge per kategori */}
          <div className="flex gap-2 overflow-x-auto pb-1 -mb-1 sm:flex-wrap sm:overflow-visible" data-testid="home-category-shortcuts">
            {CATEGORY_SHORTCUTS.map(({ label, Icon, text, chipIdle, chipActive, countIdle, countActive }) => {
              const active = category === label;
              return (
                <button
                  key={label}
                  type="button"
                  onClick={() => setCategory(active ? "Alle" : label)}
                  aria-pressed={active}
                  className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3.5 h-9 text-sm font-medium transition-colors duration-150 shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                    active ? chipActive : chipIdle
                  }`}
                  data-testid={`home-category-shortcut-${label.toLowerCase()}`}
                >
                  <Icon className={`w-4 h-4 ${text}`} aria-hidden="true" />
                  {t(label)}
                  <span
                    className={`text-xs tabular-nums rounded-full px-1.5 min-w-[1.35rem] text-center ${
                      active ? countActive : countIdle
                    }`}
                    data-testid={`home-category-count-${label.toLowerCase()}`}
                  >
                    {categoryCounts[label] || 0}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Samlet søke- og filterpanel, rett over resultatene */}
          <div className="rounded-2xl border border-border bg-card p-4 space-y-3" data-testid="home-filter-bar">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t("Søk etter arrangement, sted...")}
                  className="pl-9 h-10 bg-background"
                  data-testid="home-search-input"
                />
              </div>
              {position ? (
                <div className="flex gap-2 shrink-0">
                  <Select value={radius} onValueChange={setRadius}>
                    <SelectTrigger className="bg-background w-[150px]" data-testid="home-radius-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {["10", "25", "50", "100", "250"].map((r) => (
                        <SelectItem key={r} value={r}>{t("Innen {n} km", { n: r })}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button variant="secondary" size="icon" onClick={clearPosition} className="shrink-0" data-testid="home-clear-position-button" title={t("Fjern posisjonsfilter")}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <Button
                  variant="secondary"
                  onClick={useMyPosition}
                  disabled={locating}
                  className="shrink-0"
                  data-testid="home-use-position-button"
                >
                  {locating ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <LocateFixed className="w-4 h-4 mr-1.5" />}
                  {t("Min posisjon")}
                </Button>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="home-category-select" className="bg-background">
                  <SelectValue placeholder={t("Kategori")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Alle">{t("Alle kategorier")}</SelectItem>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>{t(c)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <PostalCodeInput
                value={postalCode}
                onChange={(v) => { setPostalCode(v); if (!v) setPostalPlace(""); }}
                onPlace={(p) => setPostalPlace(p.poststed)}
                placeholder={t("Postnummer eller sted")}
                showHint={false}
                testId="home-postalcode-input"
                className="[&_input]:bg-background"
              />
              <Input
                value={kommune}
                onChange={(e) => setKommune(e.target.value)}
                placeholder={t("Kommune")}
                className="bg-background"
                data-testid="home-kommune-input"
              />
              <Input
                value={cityFilter}
                onChange={(e) => setCityFilter(e.target.value)}
                placeholder={t("By")}
                className="bg-background"
                data-testid="home-city-input"
              />
            </div>
          </div>

          {/* Resultatlinje rett under panelet – ingen scrolling for å se om det virker */}
          <div className="flex flex-wrap items-center gap-2 min-h-[28px]" data-testid="home-result-summary">
            {loading ? (
              <span className="text-sm text-muted-foreground flex items-center gap-1.5" data-testid="home-result-loading">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> {t("Søker...")}
              </span>
            ) : (
              <span className="text-sm font-medium" data-testid="home-result-count">
                {visibleEvents.length === 0
                  ? t("Ingen treff")
                  : visibleEvents.length === 1
                    ? t("1 arrangement")
                    : t("{n} arrangementer", { n: visibleEvents.length })}
                <span className="text-muted-foreground font-normal"> · {t("sortert etter dato, nærmest først")}</span>
              </span>
            )}
            {search.trim() && (
              <Badge variant="secondary" className="gap-1" data-testid="home-chip-search">
                «{search.trim()}»
                <button type="button" onClick={() => setSearch("")} aria-label={t("Fjern søk")}><X className="w-3 h-3" /></button>
              </Badge>
            )}
            {freeOnly && (
              <>
                <Badge className="gap-1 bg-brand-soft text-foreground border border-brand/40 hover:bg-brand-soft" data-testid="home-chip-free">
                  <Gift className="w-3 h-3" /> {t("Gratis")}
                  <button type="button" onClick={() => setFreeOnly(false)} aria-label={t("Vis også betalte arrangementer")}><X className="w-3 h-3" /></button>
                </Badge>
                <button
                  type="button"
                  onClick={shareFreeLink}
                  className="inline-flex items-center gap-1 text-xs text-muted-foreground underline underline-offset-4 hover:text-foreground transition-colors duration-150"
                  data-testid="home-share-free-link-button"
                >
                  <Share2 className="w-3 h-3" /> {t("Del lenke til gratisarrangementer")}
                </button>
              </>
            )}
            {category !== "Alle" && (
              <Badge variant="outline" className={`gap-1 ${categoryStyle(category).badge}`} data-testid="home-chip-category">
                {t(category)}
                <button type="button" onClick={() => setCategory("Alle")} aria-label={t("Fjern kategori")}><X className="w-3 h-3" /></button>
              </Badge>
            )}
            {postalCode.trim() && (
              <Badge variant="secondary" className="gap-1" data-testid="home-chip-postal">
                {t("Postnr")} {postalCode}{postalPlace ? ` ${postalPlace}` : ""}
                <button type="button" onClick={() => { setPostalCode(""); setPostalPlace(""); }} aria-label={t("Fjern postnummer")}><X className="w-3 h-3" /></button>
              </Badge>
            )}
            {kommune.trim() && (
              <Badge variant="secondary" className="gap-1" data-testid="home-chip-kommune">
                {kommune}
                <button type="button" onClick={() => setKommune("")} aria-label={t("Fjern kommune")}><X className="w-3 h-3" /></button>
              </Badge>
            )}
            {cityFilter.trim() && (
              <Badge variant="secondary" className="gap-1" data-testid="home-chip-city">
                {cityFilter}
                <button type="button" onClick={() => setCityFilter("")} aria-label={t("Fjern by")}><X className="w-3 h-3" /></button>
              </Badge>
            )}
            {position && (
              <Badge variant="secondary" className="gap-1" data-testid="home-chip-position">
                <LocateFixed className="w-3 h-3" /> {t("Innen {n} km", { n: radius })}
                <button type="button" onClick={clearPosition} aria-label={t("Fjern posisjon")}><X className="w-3 h-3" /></button>
              </Badge>
            )}
          </div>
        </div>

        {error && (
          <div className="rounded-xl border border-border bg-card p-8 text-center text-muted-foreground" data-testid="home-error-state">
            {error}
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="space-y-3">
                <Skeleton className="aspect-[16/9] rounded-xl" />
                <Skeleton className="h-5 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            ))}
          </div>
        ) : visibleEvents.length === 0 && !error ? (
          <div className="rounded-xl border border-dashed border-border bg-card p-12 text-center" data-testid="home-empty-state">
            <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-brand-soft flex items-center justify-center">
              <CalendarDays className="w-7 h-7 text-brand" />
            </div>
            <h3 className="font-heading font-semibold text-lg">{t("Ingen arrangementer funnet")}</h3>
            <p className="text-sm text-muted-foreground mt-1">
              {freeOnly && !(hasLocationFilter || search || category !== "Alle")
                ? t("Ingen gratisarrangementer akkurat nå – se alle arrangementer i stedet.")
                : hasLocationFilter || search || category !== "Alle" || freeOnly
                ? t("Prøv et annet søk, sted eller større radius.")
                : t("Kom tilbake senere, eller bli arrangør og publiser ditt eget!")}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="home-event-grid">
            {visibleEvents.map((event) => (
              <EventCard key={event.id} event={event} />
            ))}
          </div>
        )}
      </section>

      {/* For nye arrangører – salgspunkter */}
      <section className="mt-14 sm:mt-20 border-t border-border bg-secondary/40" data-testid="home-organizer-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8">
            <div className="max-w-2xl space-y-3">
              <p className="text-sm font-medium text-brand uppercase tracking-wide">{t("For arrangører")}</p>
              <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight" data-testid="home-organizer-title">
                {t("Arrangerer du noe? Selg billetter med Right in.")}
              </h2>
              <p className="text-base text-muted-foreground leading-relaxed">
                {t("Publiser på minutter, del med ett trykk og sjekk inn med QR – uten gebyrpåslag for publikum.")}
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button asChild size="lg" className="h-11 px-6" data-testid="home-organizer-cta-button">
                <Link to="/registrer">
                  {t("Bli arrangør")}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
              <Button asChild variant="secondary" size="lg" className="h-11 px-6" data-testid="home-organizer-more-button">
                <Link to="/for-arrangorer">{t("Les mer for arrangører")}</Link>
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5" data-testid="home-organizer-points">
            {[
              { icon: Languages, title: t("Velg mellom nynorsk og bokmål"), text: t("Dashbordet, e-postene og billettsiden følger målformen du velger."), testId: "home-organizer-point-language" },
              { icon: Wallet, title: t("Full integrasjon med Vipps og Facebook"), text: t("Pengene går rett til din Vipps-konto, og arrangementet kan publiseres til Facebook-siden din."), testId: "home-organizer-point-integrations" },
            ].map((p) => (
              <div key={p.testId} className="rounded-2xl border border-border bg-card p-5 space-y-3 card-lift" data-testid={p.testId}>
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-brand-soft text-brand">
                  <p.icon className="w-5 h-5" aria-hidden="true" />
                </span>
                <h3 className="font-heading font-semibold text-lg leading-snug">{p.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{p.text}</p>
              </div>
            ))}
            <a
              href="mailto:hei@rightin.no"
              className="rounded-2xl border border-border bg-card p-5 space-y-3 card-lift block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              data-testid="home-organizer-point-contact"
            >
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-brand-soft text-brand">
                <Mail className="w-5 h-5" aria-hidden="true" />
              </span>
              <h3 className="font-heading font-semibold text-lg leading-snug">{t("Ta kontakt")}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {t("Lurer du på noe? Send oss en e-post, så svarer vi raskt.")}
              </p>
              <span className="inline-flex items-center gap-1.5 text-sm font-medium text-brand underline underline-offset-4" data-testid="home-organizer-contact-email">
                hei@rightin.no
                <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
              </span>
            </a>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
